import React, { useEffect, useState } from 'react';
import { FileText, Download, Search } from 'lucide-react';
import { getProducts } from '../lib/api';
import { AVAILABLE_COAS, coaHref } from '../lib/coas';

const COAsPage = () => {
  const [q, setQ] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  // Exclude accessories (water, needles) — they don't need a COA
  const filtered = products
    .filter((p) => p.category !== 'Accessories' && p.category !== 'Supplements')
    .filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14">
        <p className="text-[#909090] text-sm uppercase tracking-widest mb-3">Independent third-party analysis</p>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Lab Tested COAs</h1>
        <p className="text-zinc-400 max-w-3xl text-lg">
          Every Hydro Formula peptide ships with a batch-specific Certificate
          of Analysis. Search the database below.
        </p>

        <div className="relative mt-8 max-w-md">
          <Search size={18} className="absolute top-1/2 left-3 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by product name..."
            className="w-full bg-zinc-950 border border-zinc-800 text-white pl-11 pr-4 py-3 rounded focus:outline-none focus:border-[#909090]"
          />
        </div>

        <div className="mt-10 border border-zinc-900 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-400 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left py-3 px-4">Product</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Batch</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Purity</th>
                <th className="text-right py-3 px-4">COA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {filtered.map((p) => {
                const hasCoa = AVAILABLE_COAS.has(p.slug);
                const pdfHref = coaHref(p.slug);
                return (
                  <tr key={p.id} className="hover:bg-zinc-950/60">
                    <td className="py-3 px-4 text-white">{p.name}</td>
                    <td className="py-3 px-4 text-zinc-400 hidden md:table-cell">HH-{String(p.id).padStart(4, '0')}</td>
                    <td className="py-3 px-4 text-green-400 hidden md:table-cell">≥99.{(p.id % 9)}%</td>
                    <td className="py-3 px-4 text-right">
                      {hasCoa ? (
                        <a
                          href={pdfHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`coa-view-${p.slug}`}
                          className="inline-flex items-center gap-1.5 text-[#909090] hover:text-white text-sm transition-colors"
                        >
                          <FileText size={14} /> View <Download size={14} />
                        </a>
                      ) : (
                        <span
                          data-testid={`coa-pending-${p.slug}`}
                          className="inline-flex items-center gap-1.5 text-zinc-600 text-sm italic"
                        >
                          Coming soon
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-zinc-500">No COAs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default COAsPage;
